import { sql } from "drizzle-orm";
import {
  check,
  foreignKey,
  index,
  integer,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  varchar,
} from "drizzle-orm/pg-core";
import {
  organisations,
  tutorInvitations,
  tutorProfiles,
  users,
} from "./schemaBase.js";

export interface TutorBookingSetup {
  firstName: string;
  lastName: string;
  locationId: number | null;
}

export type TutorProvisioningStatus =
  | "pending"
  | "processing"
  | "linked"
  | "needs_attention";

/** One durable identity intent per profile; external writes are never blindly replayed. */
export const tutorProvisioning = pgTable(
  "tutor_provisioning",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    organisationId: varchar("organisation_id").notNull(),
    tutorProfileId: varchar("tutor_profile_id").notNull(),
    requestedByUserId: varchar("requested_by_user_id"),
    invitationId: varchar("invitation_id"),
    firstName: text("first_name").notNull(),
    lastName: text("last_name").notNull(),
    email: text("email").notNull(),
    locationId: integer("location_id"),
    status: text("status")
      .$type<TutorProvisioningStatus>()
      .notNull()
      .default("pending"),
    externalProviderId: integer("external_provider_id"),
    creationAttemptedAt: timestamp("creation_attempted_at", {
      withTimezone: true,
    }),
    claimId: varchar("claim_id"),
    availableAt: timestamp("available_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    attempts: integer("attempts").notNull().default(0),
    lastError: text("last_error"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    foreignKey({
      name: "tutor_provisioning_org_fk",
      columns: [t.organisationId],
      foreignColumns: [organisations.id],
    }),
    foreignKey({
      name: "tutor_provisioning_profile_fk",
      columns: [t.tutorProfileId],
      foreignColumns: [tutorProfiles.id],
    }).onDelete("cascade"),
    foreignKey({
      name: "tutor_provisioning_requester_fk",
      columns: [t.requestedByUserId],
      foreignColumns: [users.id],
    }).onDelete("set null"),
    foreignKey({
      name: "tutor_provisioning_invitation_fk",
      columns: [t.invitationId],
      foreignColumns: [tutorInvitations.id],
    }).onDelete("set null"),
    uniqueIndex("tutor_provisioning_profile_idx").on(t.tutorProfileId),
    index("tutor_provisioning_due_idx").on(
      t.organisationId,
      t.status,
      t.availableAt,
    ),
    check(
      "tutor_provisioning_status_check",
      sql`${t.status} in ('pending', 'processing', 'linked', 'needs_attention')`,
    ),
    check(
      "tutor_provisioning_location_check",
      sql`${t.locationId} is null or ${t.locationId} > 0`,
    ),
  ],
);
